<template>
  <div class="max-w-7xl mx-auto px-4 py-8 font-inter">
    <div class="mb-6">
      <router-link
        :to="{ name: 'TalentCommunityList' }" 
        class="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft class="h-4 w-4" />
        Back to Talent Communities
      </router-link>
    </div>

    <div v-if="talentPoolsStore.isLoadingActivePool" class="animate-pulse space-y-6">
      <div class="h-6 w-48 rounded bg-muted"></div>
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div class="lg:col-span-2 space-y-6">
          <div class="rounded-lg border border-border bg-card p-6">
            <div class="h-5 w-1/3 rounded bg-muted"></div>
            <div class="mt-4 h-4 w-2/3 rounded bg-muted"></div>
            <div class="mt-6 h-3 w-full rounded bg-muted"></div>
            <div class="mt-2 h-3 w-5/6 rounded bg-muted"></div>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div class="h-20 rounded-lg border border-border bg-card"></div>
            <div class="h-20 rounded-lg border border-border bg-card"></div>
            <div class="h-20 rounded-lg border border-border bg-card"></div>
          </div>
        </div>
        <div class="space-y-6">
          <div class="h-40 rounded-lg border border-border bg-card"></div>
          <div class="h-40 rounded-lg border border-border bg-card"></div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8" v-else-if="talentPoolsStore.activePool">

      <div class="lg:col-span-2 space-y-8">
        
        <div class="flex flex-col">
          <!-- Header containing talent pool title and details-->
          <div class="flex flex-col md:flex-row md:justify-between md:items-center">
            <h1 class="text-3xl font-bold text-gray-900 leading-tight pr-4">{{ talentPoolsStore.activePool.title }}</h1>

            <div class="bg-newPrimary text-white text-sm font-semibold px-2.5 py-0.5 rounded-full flex items-center justify-center flex-shrink-0">
              {{ talentPoolsStore.activePool?.project_id ? 'Project Talent Pool' : 'BAU Talent Pool' }}
            </div>
          </div>

          <!-- Talent pool subtitle (company name, location, creation date)-->
          <div class="flex flex-col md:flex-row gap-4 text-gray-600">
            <RouterLink
              :to="{ name: 'ContractorCompanyPage', params: { slug: talentPoolsStore.activePool.company_slug } }"
            >
              <span class="font-semibold text-gray-800 hover:cursor-pointer hover:text-newPrimary">{{ talentPoolsStore.activePool.company_name }}</span>
            </RouterLink>
            <div class="flex gap-1 items-center">
              <MapPin class="text-newPrimary h-5 w-5"/>
              <span class="">{{ talentPoolsStore.activePool.location }}</span>
            </div>
            <div class="flex gap-1 items-center">
              <Calendar class="text-newPrimary h-5 w-5"/>
              <span class="">{{ formattedCreationDate }}</span>
            </div>
          </div>
        </div>

        <!-- Custom stats bar implementation -->
         <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div class="bg-card border border-border rounded-lg shadow-sm flex items-center px-6 py-4 gap-3">
            <FileText class="text-newPrimary h-6 w-6" />

            <div class="flex-1">
              <p class="text-sm text-gray-500">Work Type</p>
              <p class="font-semibold text-gray-900 leading-tight">{{ talentPoolsStore.activePool.work_type }}</p>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm flex items-center px-6 py-4 gap-3">
            <DollarSign class="text-newPrimary h-6 w-6" />

            <div class="flex-1">
              <p class="text-sm text-gray-500">Salary</p>
              <p class="font-semibold text-gray-900 leading-tight">Competitive</p>
            </div>
          </div>

          <div class="bg-card border border-border rounded-lg shadow-sm flex items-center px-6 py-4 gap-3">
            <Clock class="text-newPrimary h-6 w-6" />

            <div class="flex-1">
              <p class="text-sm text-gray-500">Hiring By</p>
              <p class="font-semibold text-gray-900 leading-tight">{{ formattedTimelineDate }}</p>
            </div>
          </div>
        </div>

        <!-- Talent Pool Description -->
        <div class="bg-card border border-border rounded-lg shadow-sm flex flex-col px-6 py-4 prose prose-sm max-w-none">
          <h3 class="font-semibold text-gray-900">About this Talent Pool</h3>
          <p class="prose prose-sm" v-html="talentPoolsStore.activePool.description_html"></p>
        </div>

        <!-- Project information -->
         <div v-if="talentPoolsStore.activePool.project" class="bg-card border border-border rounded-lg shadow-sm flex flex-col px-6 py-4 prose prose-sm max-w-none">
          <h3 class="font-semibold text-gray-900">Project Details</h3>
          <h4 class="text-lg">{{ talentPoolsStore.activePool?.project.name }}</h4>
          <p>{{ talentPoolsStore.activePool?.project.summary }}</p>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            <div>
                <span class="text-xs text-gray-500 block">Budget</span>
                <span class="font-medium text-gray-700">${{ Number(talentPoolsStore.activePool.project.budget).toLocaleString() }}</span>
              </div>
              <div>
                <span class="text-xs text-gray-500 block">Start Date</span>
                <span class="font-medium text-gray-700">{{ talentPoolsStore.activePool.project.start_date }}</span>
              </div>
              <div>
                <span class="text-xs text-gray-500 block">Expected Length</span>
                <span class="font-medium text-gray-700">{{ talentPoolsStore.activePool.project.length }}</span>
              </div>
          </div>
        </div>

        <!-- Map -->
        <div class="bg-card border border-border rounded-lg shadow-sm px-6 py-4 flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <MapPin class="text-newPrimary h-6 w-6" />
            <h3 class="text-lg font-semibold text-gray-900">Location</h3>
          </div>

          <GoogleMap v-if="talentPoolsStore.activePool.location_lat" :lat="Number(talentPoolsStore.activePool.location_lat)" :lng="Number(talentPoolsStore.activePool?.location_lng)"  />
        </div>
      </div>

      <div class="lg:col-span-1 space-y-6">

        <div class="bg-card border border-border rounded-lg shadow-sm flex flex-col px-6 py-4 space-y-4">
          <h3 class="font-semibold text-gray-900">Application status</h3>
          <div class="flex border border-border rounded-md px-4 py-2 items-center space-x-2">
            <span class="rounded-full h-3 w-3" :class="applicationStatus.statusClasses"></span>
            <span class="font-semibold text-gray-500" :class="applicationStatus.textClasses">{{ applicationStatus.text }}</span>
          </div>
          <hr />


          <!-- Apply button-->
          <div @click="applyNow" v-if="!talentPoolsStore.activePool?.applicationStatus" class="bg-newPrimary rounded-md text-white text-center px-4 py-2 transition hover:brightness-110 hover:cursor-pointer">Apply Now</div>
          <!-- Withdraw button-->
          <div @click="withdrawApplication" v-else-if="talentPoolsStore.activePool?.applicationStatus && talentPoolsStore.activePool?.applicationStatus === 'Applied'" class="bg-destructive/80 rounded-md text-white text-center px-4 py-2 transition hover:bg-destructive hover:cursor-pointer">Withdraw Application</div>
          <span v-if="!talentPoolsStore.activePool?.applicationStatus" class="text-center text-gray-500 md:px-10 text-sm">Join this talent pool to be considered for future opportunities</span>
        </div>

        <!-- Talent Pool Requirements -->
        <div class="bg-card border border-border rounded-lg shadow-sm flex flex-col px-6 py-4 space-y-2">
          <h3 class="font-semibold text-gray-900">Requirements</h3>
          <div class="flex flex-wrap gap-2">
            <template v-if="talentPoolsStore.activePool.requirements?.length > 0">
              <span v-for="req in talentPoolsStore.activePool.requirements" :key="req" class="bg-newPrimary text-white text-sm font-semibold px-2.5 py-0.5 rounded-full flex items-center justify-center flex-shrink-0">
                {{ req }}
              </span>
            </template>
            <template v-else>
              <p class="text-sm text-gray-500 italic">No specific pre-employment requirements are listed for this talent pool.</p>
            </template>
          </div>
        </div>

        <!-- Talent Pool Skills -->
        <div class="bg-card border border-border rounded-lg shadow-sm flex flex-col px-6 py-4 space-y-2">
          <h3 class="font-semibold text-gray-900">Skills & Qualifications</h3>
          <div class="flex flex-wrap gap-2">
            <template v-if="talentPoolsStore.activePool.qualifications?.length > 0">
              <span v-for="skill in talentPoolsStore.activePool.qualifications" :key="skill" class="bg-newPrimary text-white text-sm font-semibold px-2.5 py-0.5 rounded-full flex items-center justify-center flex-shrink-0">
                {{ skill }}
              </span>
            </template>
            <template v-else>
              <p class="text-sm text-gray-500 italic">This is an open talent pool. All skill sets are welcome to apply.</p>
            </template>
          </div>
        </div>
        
        <div class="bg-white border border-gray-200 rounded-lg shadow-sm hidden">
          <h3 class="font-semibold text-gray-900 p-6 border-b border-gray-200">Messages</h3>
          
          <div class="p-6 space-y-4 max-h-96 overflow-y-auto">
            <div v-for="message in messages" :key="message.id">
              <div v-if="message.sender === 'company'" class="flex justify-start">
                <div class="bg-gray-100 rounded-lg px-4 py-3 max-w-xs">
                  <p class="text-sm text-gray-800">{{ message.text }}</p>
                  <span class="text-xs text-gray-500 block text-right mt-1">{{ message.time }}</span>
                </div>
              </div>
              <div v-else class="flex justify-end">
                <div class="bg-blue-600 text-white rounded-lg px-4 py-3 max-w-xs">
                  <p class="text-sm">{{ message.text }}</p>
                  <span class="text-xs text-blue-200 block text-right mt-1">{{ message.time }}</span>
                </div>
              </div>
            </div>
            
            <div v-if="messages.length === 0" class="text-center py-8">
              <p class="text-sm text-gray-400">No messages yet.</p>
            </div>
          </div>
          
          <div class="p-4 border-t border-gray-200 bg-gray-50">
            <div class="relative">
              <textarea 
                rows="3" 
                class="w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500" 
                placeholder="Type your message..."
              ></textarea>
              <button class="absolute bottom-3 right-3 px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded hover:bg-blue-700">
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="rounded-lg border border-border bg-card px-6 py-12 text-center text-gray-500"
    >
      Talent pool not found.
    </div>
  </div>
</template>

<script setup>
// ===================================
// 1. IMPORTS
// ===================================

// ===================================
// 2. PROPS & EMITS
// ===================================

// ===================================
// 3. STATE
// ===================================
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { useTalentPoolsStore } from '@/stores/talentPoolsStore'
import { ArrowLeft, DollarSign, Briefcase, Clock, Calendar, Circle, CheckCircle, MapPin, FileText, Building2, ArrowRightLeft } from 'lucide-vue-next';
import { StatsBar } from '@/components/marketplace/talentPools'
import { formatDate, formatHiringTimeline } from '@/utils/formatters';
import GoogleMap from '@/components/marketplace/generic/GoogleMap'
import TalentPoolStats from '@/components/TalentPoolStats.vue';

const talentPoolsStore = useTalentPoolsStore()
const route = useRoute()

const stats = [
    { label: 'Salary', value: '$120,000', color: 'green' },
    { label: 'Work Type', value: 'Contract', color: 'blue' },
    { label: 'Status', value: 'Open', color: '' },
  ];

// ===================================
// 4. COMPUTED PROPERTIES
// ===================================
const formattedTimelineDate = computed(() => {
  const value = formatHiringTimeline(
    talentPoolsStore.activePool?.hiring_timeline,
    talentPoolsStore.activePool?.timeline_date,
    talentPoolsStore.activePool?.timeline
  )
  if (!value) return value
  return value
    .replace(/^Hiring\s+from\s+/i, 'From ')
    .replace(/^Hiring\s+/i, '')
})

const formattedCreationDate = computed(() => {
  return formatDate(talentPoolsStore.activePool?.creation_date)
})

const applicationStatus = computed(() => {
  const status = talentPoolsStore.activePool?.applicationStatus

  switch (status) {
    case 'Applied':
      return { text: 'Applied', statusClasses: 'bg-newPrimary', textClasses: 'text-newPrimary' }
    case 'Accepted':
      return { text: 'Accepted', statusClasses: 'bg-newPrimary', textClasses: 'text-newPrimary' }
    default:
      return { 'text': 'Not Applied', statusClasses: 'bg-gray-500', textClasses: 'text-gray-500' }
  }
})

// ===================================
// 5. WATCHERS
// ===================================

// ===================================
// 6. LIFECYCLE HOOKS
// ===================================
onMounted(() => {
  if (route.params.id) {
    talentPoolsStore.fetchTalentPoolById(route.params.id, "contractor")
  }
})

// onUnmounted(() => {
//   // ...
// });


// ===================================
// 7. FUNCTIONS
// ===================================

// Import all the icons you'll need
// This dummy object would be passed as a prop from the parent route component
const messages = [
    { id: 1, sender: 'company', text: "Hi! We've received your application and our team is currently reviewing it. We'll be in touch soon.", time: "10:30 AM" },
    { id: 2, sender: 'you', text: "Great, thank you for the update!", time: "10:32 AM" }
]

const applyNow = () => {
  if (!talentPoolsStore.activePool?.applicationStatus && talentPoolsStore.activePool?.id) {
    talentPoolsStore.applyToTalentPool(talentPoolsStore.activePool.id)
  }
}

const withdrawApplication = () => {
  if (talentPoolsStore.activePool?.applicationStatus  && talentPoolsStore.activePool.applicationStatus === 'Applied' && talentPoolsStore.activePool?.id) {
    talentPoolsStore.withdrawApplication(talentPoolsStore.activePool.id)
  }
}

</script>
